import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Card, Col, Row, Segmented, Space, Statistic, Tabs, Typography } from 'antd';
import {
  CrownOutlined,
  AppstoreOutlined,
  TagsOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { get } from '../../api/client';
import { LevelsTab } from './components/LevelsTab';
import { FeaturesManager } from './components/FeaturesManager';
import { FeatureMatrix } from './components/FeatureMatrix';
import { PlansTab } from './components/PlansTab';
import { SubscriptionsTab } from './components/SubscriptionsTab';
import { CampaignsTab } from './components/CampaignsTab';
import { GrantsTab } from './components/GrantsTab';
import { BatchGrantPanel } from './components/BatchGrantPanel';
import { PromotionLinksTab } from './components/PromotionLinksTab';
import { ReferralRecordsTab } from './components/ReferralRecordsTab';
import { ReferralRulePanel, SignupGiftRulePanel } from './components/MembershipRulePanels';
import type { LevelRow, PlanRow, FeatureRow, SubscriptionListResponse } from './types';

const TEAL = '#0d9488';

/** 顶部统计卡配置 */
const STAT_CARDS = [
  { key: 'levels', title: '会员等级', icon: <CrownOutlined />, color: '#0d9488' },
  { key: 'plans', title: '启用套餐', icon: <TagsOutlined />, color: '#6366f1' },
  { key: 'features', title: '功能权限项', icon: <AppstoreOutlined />, color: '#f59e0b' },
  { key: 'users', title: '订阅用户', icon: <TeamOutlined />, color: '#ec4899' },
] as const;

const TAB_KEYS = ['levels', 'features', 'plans', 'subscriptions', 'gift', 'growth', 'grants'] as const;
type TabKey = (typeof TAB_KEYS)[number];

/**
 * 「赠送与活动」「推广与邀请」各自收着几条同类事情，用一栏 Segmented 切换而不是再开一层标签页：
 * 标签页里嵌标签页在窄屏会把两行导航挤成一团，而这三件事本来就是「同一件事的三种做法」。
 */
const GIFT_VIEWS: { label: string; value: string }[] = [
  { label: '活动管理', value: 'campaigns' },
  { label: '注册赠送规则', value: 'rules' },
  { label: '批量赠送', value: 'batch' },
];

const GROWTH_VIEWS: { label: string; value: string }[] = [
  { label: '推广链接', value: 'promotion' },
  { label: '邀请记录', value: 'referral' },
  { label: '邀请规则', value: 'referral-rules' },
];

const DEFAULT_VIEW: Record<string, string> = { gift: 'campaigns', growth: 'promotion' };

/**
 * 会员管理：会员域的唯一入口。
 *
 * 布局按「先定档位、再定卖法、再看人、最后看账」的次序分七栏：
 * 会员等级 / 功能权限 / 会员套餐 / 订阅用户 / 赠送与活动 / 推广与邀请 / 发放记录。
 * 原来散在三处的会员读写口（系统配置里的「会员赠送」、独立的推广链接页与邀请记录页）都收进来，
 * 菜单与路由不再各留一份——同一件事有两个入口，改一次就要对两次。
 *
 * 当前栏与子栏都写在 URL 上（?tab=&view=），旧的 /promotion-links、/referrals 重定向到这里，
 * 收藏过的地址照样落在原来那栏。
 */
export default function MembershipPage() {
  const [params, setParams] = useSearchParams();
  const urlTab = params.get('tab');
  const tab: TabKey = (TAB_KEYS as readonly string[]).includes(urlTab ?? '') ? (urlTab as TabKey) : 'levels';
  const view = params.get('view') ?? DEFAULT_VIEW[tab] ?? '';

  const applyParams = (next: URLSearchParams) => setParams(next, { replace: true });

  const switchTab = (next: string) => {
    const p = new URLSearchParams(params);
    p.set('tab', next);
    // 子栏只属于这两栏，切走就清掉，免得 ?view=promotion 挂到「会员等级」上
    if (DEFAULT_VIEW[next]) p.set('view', DEFAULT_VIEW[next]);
    else p.delete('view');
    applyParams(p);
  };

  const switchView = (next: string) => {
    const p = new URLSearchParams(params);
    p.set('view', next);
    applyParams(p);
  };

  const { data: levels } = useQuery({
    queryKey: ['membership-levels'],
    queryFn: () => get<LevelRow[]>('/admin/membership/levels'),
  });
  const { data: plans } = useQuery({
    queryKey: ['membership-plans'],
    queryFn: () => get<PlanRow[]>('/admin/membership/plans'),
  });
  const { data: features } = useQuery({
    queryKey: ['membership-features'],
    queryFn: () => get<FeatureRow[]>('/admin/membership/features'),
  });
  const { data: subs } = useQuery({
    queryKey: ['membership-subscriptions-count'],
    queryFn: () => get<SubscriptionListResponse>('/admin/membership/subscriptions', { page: 1, pageSize: 1 }),
  });

  const statValues: Record<string, number> = {
    levels: levels?.length ?? 0,
    plans: plans?.filter((p) => p.isActive).length ?? 0,
    features: features?.length ?? 0,
    users: subs?.total ?? 0,
  };

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Typography.Title level={4} style={{ marginBottom: 4 }}>
          会员管理
        </Typography.Title>
        <Typography.Text type="secondary">
          会员域的唯一入口：档位与权益、售卖套餐、赠送与活动、推广与邀请、用户订阅，以及每一条会员时长的发放流水。
        </Typography.Text>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        {STAT_CARDS.map((s) => (
          <Col key={s.key} xs={12} lg={6}>
            <Card styles={{ body: { padding: 20 } }}>
              <Space align="center" size={16}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 14,
                    background: `${s.color}1a`,
                    color: s.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 22,
                  }}
                >
                  {s.icon}
                </div>
                <Statistic title={s.title} value={statValues[s.key]} styles={{ content: { color: '#1f2329', fontWeight: 600 } }} />
              </Space>
            </Card>
          </Col>
        ))}
      </Row>

      <Card styles={{ body: { paddingTop: 8 } }}>
        <Tabs
          activeKey={tab}
          onChange={switchTab}
          items={[
            { key: 'levels', label: '会员等级', children: <LevelsTab /> },
            {
              key: 'features',
              label: '功能权限',
              children: (
                <div>
                  <FeaturesManager />
                  <FeatureMatrix />
                </div>
              ),
            },
            { key: 'plans', label: '会员套餐', children: <PlansTab /> },
            { key: 'subscriptions', label: '订阅用户', children: <SubscriptionsTab /> },
            {
              key: 'gift',
              label: '赠送与活动',
              children: (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  <Segmented options={GIFT_VIEWS} value={view} onChange={(v) => switchView(String(v))} />
                  {view === 'rules' ? (
                    <SignupGiftRulePanel />
                  ) : view === 'batch' ? (
                    <BatchGrantPanel />
                  ) : (
                    <CampaignsTab />
                  )}
                </Space>
              ),
            },
            {
              key: 'growth',
              label: '推广与邀请',
              children: (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  <Segmented options={GROWTH_VIEWS} value={view} onChange={(v) => switchView(String(v))} />
                  {view === 'referral' ? (
                    <ReferralRecordsTab />
                  ) : view === 'referral-rules' ? (
                    <ReferralRulePanel />
                  ) : (
                    <PromotionLinksTab />
                  )}
                </Space>
              ),
            },
            { key: 'grants', label: '发放记录', children: <GrantsTab /> },
          ]}
        />
      </Card>

      <Typography.Text type="secondary" style={{ display: 'block', marginTop: 16, fontSize: 12, color: TEAL }}>
        说明：功能权限矩阵决定各等级可用能力，用户端权限由服务端下发；会员时长的一切发放（购买、活动、注册赠送、推广与邀请奖励、批量补偿）都走同一条通路并留一行流水。
      </Typography.Text>
    </div>
  );
}
